"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { UNIFIED_INBOX_QUERY_KEY } from "@/hooks/queries/unified-inbox"
import { replyToEmailThreadAction } from "@/server-actions/email-reply"

export interface ReplyToEmailThreadInput {
  inboxId: string
  threadId: string
  text: string
}

/**
 * Answering a customer's email by hand.
 *
 * The outcomes are not all successes or all failures, so this does not go through
 * `withQueryHandling`: a refused send must reject, so the composer keeps the
 * draft, while «sent but not recorded» and «sent, draft kept» are successes that
 * still have something to say. `unknown` is the one that matters most — it
 * succeeds, deliberately, so the operator is not handed a retry that could send
 * the same answer twice.
 */
export function useReplyToEmailThreadMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ inboxId, threadId, text }: ReplyToEmailThreadInput) => {
      const result = await replyToEmailThreadAction({
        inbox_id: inboxId,
        thread_id: threadId,
        body: text,
      })

      if ("status" in result) throw new Error(result.errorMessage)

      // `unknown` resolves — the operator must not be handed a one-click retry
      // that could send the same answer twice — but it keeps their text, because
      // if the mail did NOT leave, this is the only copy of it.
      return { ...result, keepDraft: result.outcome === "unknown" }
    },
    onSuccess: (result, { inboxId, threadId }) => {
      if (result.outcome === "sent") toast.success(result.message)
      // Everything else went out but is worth reading, so it holds the screen.
      else toast.warning(result.message, { duration: 10_000 })

      queryClient.invalidateQueries({
        queryKey: [...UNIFIED_INBOX_QUERY_KEY, "thread", `email:${inboxId}:${threadId}`],
      })
      queryClient.invalidateQueries({ queryKey: [...UNIFIED_INBOX_QUERY_KEY, "list"] })
    },
  })
}
